import React, { useCallback, useEffect, useId, useRef, useState } from 'react';
import { getSaenggibuLibraryMaterials } from '../../api/saenggibuApi';
import { trackSaenggibuUxEvent } from '../../utils/eventTracker';
import SaenggibuLibraryMaterialList from './SaenggibuLibraryMaterialList';
import './SaenggibuAttachMenu.css';

const MENU_ITEM_SELECTOR = '[role="menuitem"]';

/**
 * 생기부 폼·일괄 참고자료의 첨부 버튼에 "자료함에서 고르기" 를 합친 메뉴 (Phase 4c, chat FileAttachmentButton 과 같은 방식).
 *
 * - 자료함에 자료가 1개 이상일 때만 첨부 버튼 클릭이 2항목 메뉴("파일 올리기" / "자료함에서 고르기 (N)")를 띄운다.
 * - 자료가 없거나 비로그인·조회 실패(fail-open)·libraryEnabled=false 면 클릭이 예전처럼 곧바로 onUploadClick — 요소·클릭 하나도 늘지 않는다.
 * - "자료함에서 고르기" → 버튼 아래 인라인 목록(모달 아님, 계획서류 먼저). 고르면 onPick(material) 한 번 + saenggibu_library_pick 이벤트.
 * - 버튼 자체는 renderTrigger(triggerProps) 로 호출 쪽이 그린다(폼은 점선 div, 일괄은 button) — 기존 모양·드래그앤드롭을 그대로 둔다.
 * - a11y: aria-haspopup="menu"/aria-expanded, Esc 닫고 포커스 복귀, 바깥 클릭 닫힘, ↑/↓ 이동, 항목 44px.
 */
const SaenggibuAttachMenu = ({
  currentUser,
  category,
  isBatch = false,
  disabled = false,
  libraryEnabled = true,
  onPick,
  onUploadClick,
  renderTrigger,
  className = '',
  // 생기부 밖(채점 기준서·평가계획서)에서 재사용할 때 이벤트를 바꿔 끼운다. 없으면 생기부 이벤트.
  onTrackPick,
}) => {
  const [materials, setMaterials] = useState(null); // null = 아직 안 불러옴
  const [menuOpen, setMenuOpen] = useState(false);
  const [listOpen, setListOpen] = useState(false);
  const rootRef = useRef(null);
  const triggerRef = useRef(null);
  const menuRef = useRef(null);
  const menuId = useId();
  const listId = useId();
  const uid = currentUser?.uid || null;

  useEffect(() => {
    if (!uid) {
      setMaterials(null);
      return undefined;
    }
    let cancelled = false;
    // 동기 예외(모킹·네트워크 계층)도 rejection 으로 흘려 fail-open 한 경로로 모은다
    Promise.resolve()
      .then(() => getSaenggibuLibraryMaterials())
      .then((data) => {
        if (cancelled) return;
        setMaterials(Array.isArray(data?.materials) ? data.materials : []);
      })
      .catch(() => {
        // 조회 실패는 기능을 조용히 숨긴다 (fail-open)
        if (!cancelled) setMaterials([]);
      });
    return () => {
      cancelled = true;
    };
  }, [uid]);

  const libraryCount = Array.isArray(materials) ? materials.length : 0;
  const hasLibraryMenu = Boolean(uid) && libraryEnabled && typeof onPick === 'function' && libraryCount > 0;
  const isOpen = menuOpen || listOpen;

  const closeAll = useCallback((restoreFocus = false) => {
    setMenuOpen(false);
    setListOpen(false);
    if (restoreFocus) triggerRef.current?.focus?.();
  }, []);

  useEffect(() => {
    if (!isOpen) return undefined;
    const handlePointerDown = (event) => {
      if (!rootRef.current?.contains(event.target)) closeAll(false);
    };
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        event.stopPropagation();
        closeAll(true);
      }
    };
    document.addEventListener('mousedown', handlePointerDown, true);
    document.addEventListener('touchstart', handlePointerDown, true);
    document.addEventListener('keydown', handleKeyDown, true);
    return () => {
      document.removeEventListener('mousedown', handlePointerDown, true);
      document.removeEventListener('touchstart', handlePointerDown, true);
      document.removeEventListener('keydown', handleKeyDown, true);
    };
  }, [isOpen, closeAll]);

  useEffect(() => {
    if ((disabled || !hasLibraryMenu) && isOpen) closeAll(false);
  }, [disabled, hasLibraryMenu, isOpen, closeAll]);

  useEffect(() => {
    if (menuOpen) menuRef.current?.querySelector(MENU_ITEM_SELECTOR)?.focus();
  }, [menuOpen]);

  const handleTriggerClick = (event) => {
    if (disabled) return;
    if (hasLibraryMenu) {
      event?.preventDefault?.();
      event?.stopPropagation?.();
      if (isOpen) {
        closeAll(false);
      } else {
        setMenuOpen(true);
      }
      return;
    }
    onUploadClick?.(event);
  };

  const handleMenuKeyDown = (event) => {
    const keys = ['ArrowDown', 'ArrowUp', 'Home', 'End'];
    if (!keys.includes(event.key)) return;
    const items = Array.from(menuRef.current?.querySelectorAll(MENU_ITEM_SELECTOR) || []);
    if (items.length === 0) return;
    event.preventDefault();
    const index = items.indexOf(document.activeElement);
    let next = 0;
    if (event.key === 'ArrowDown') next = index < 0 ? 0 : (index + 1) % items.length;
    else if (event.key === 'ArrowUp') next = index < 0 ? items.length - 1 : (index - 1 + items.length) % items.length;
    else if (event.key === 'End') next = items.length - 1;
    items[next].focus();
  };

  const handleUploadItem = (event) => {
    event.stopPropagation();
    closeAll(false);
    onUploadClick?.(event);
  };

  const handleLibraryItem = (event) => {
    event.stopPropagation();
    setMenuOpen(false);
    setListOpen(true);
  };

  const handlePick = (material) => {
    closeAll(false);
    if (typeof onTrackPick === 'function') {
      onTrackPick(material);
    } else {
      trackSaenggibuUxEvent('saenggibu_library_pick', {
        saenggibu_category: category,
        is_batch: isBatch,
        plan_like: Boolean(material.plan_like),
      });
    }
    onPick?.(material);
  };

  const triggerProps = {
    ref: triggerRef,
    onClick: handleTriggerClick,
    'aria-haspopup': hasLibraryMenu ? 'menu' : undefined,
    'aria-expanded': hasLibraryMenu ? isOpen : undefined,
    'aria-controls': menuOpen ? menuId : (listOpen ? listId : undefined),
  };

  return (
    <div
      ref={rootRef}
      className={`sg-attach${className ? ` ${className}` : ''}`}
      data-testid="saenggibu-attach-menu"
      data-library-count={hasLibraryMenu ? libraryCount : 0}
    >
      {renderTrigger(triggerProps)}

      {menuOpen && (
        <div
          id={menuId}
          ref={menuRef}
          className="sg-attach-menu"
          role="menu"
          aria-label="첨부 방법"
          onKeyDown={handleMenuKeyDown}
        >
          <button type="button" role="menuitem" className="sg-attach-menu__item" onClick={handleUploadItem}>
            파일 올리기
          </button>
          <button type="button" role="menuitem" className="sg-attach-menu__item" onClick={handleLibraryItem}>
            자료함에서 고르기
            <span className="sg-attach-menu__count" aria-label={`${libraryCount}개`}>{libraryCount}</span>
          </button>
        </div>
      )}

      {listOpen && (
        <div className="sg-attach-popover">
          <SaenggibuLibraryMaterialList
            id={listId}
            materials={materials}
            onPick={handlePick}
            onNavigate={() => closeAll(false)}
          />
        </div>
      )}
    </div>
  );
};

export default SaenggibuAttachMenu;
